import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loading, EmptyState } from "@/components/common/Loading";
import { SECTIONS, ItemCard, PER_CATEGORY_FULL } from "@/pages/public/SearchResults";
import { trackResult } from "@/lib/analytics";
import { useAnalyticsImpressions } from "@/hooks/useAnalyticsImpressions";

const SECTION_BY_KEY = Object.fromEntries(SECTIONS.map((s) => [s.key, s]));

export default function SearchCategory() {
  const { category } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const section = SECTION_BY_KEY[category];
  const [items, setItems] = useState(null);
  const bindImpression = useAnalyticsImpressions("search_impression");

  useEffect(() => {
    if (!section || !q.trim()) { setItems([]); return undefined; }
    let ok = true;
    setItems(null);
    api.get("/search", { params: { q: q.trim(), limit: PER_CATEGORY_FULL, surface: "search_category" } })
      .then(({ data }) => { if (ok) setItems(data?.[section.key] || []); })
      .catch(() => { if (ok) setItems([]); });
    return () => { ok = false; };
  }, [section, q]);

  const backHref = q ? `/search?q=${encodeURIComponent(q)}` : "/search";

  if (!section) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <EmptyState icon={Search} title="Unknown category" description="That category doesn't exist." action={<Link to="/search"><Button>Back to search</Button></Link>} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8" data-testid={`search-category-${section.key}`}>
      <button
        onClick={() => navigate(backHref)}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        data-testid="search-category-back"
      >
        <ArrowLeft className="h-4 w-4" /> Return to search
      </button>
      <div className="mt-4 flex items-center gap-3">
        <section.Icon className="h-6 w-6 text-primary" />
        <h1 className="text-3xl font-[720] tracking-[-0.02em] text-foreground">{section.label}</h1>
      </div>
      <p className="mt-1 text-muted-foreground">
        {q ? <>All {section.label.toLowerCase()} matching &ldquo;{q}&rdquo;.</> : `Browse ${section.label.toLowerCase()}.`}
      </p>

      <div className="mt-8">
        {items === null ? (
          <Loading label="Loading" />
        ) : items.length === 0 ? (
          <EmptyState icon={section.Icon} title={`No ${section.label.toLowerCase()} found`} description="Try a different keyword or return to search." action={<Link to={backHref}><Button variant="secondary">Return to search</Button></Link>} testId="search-category-empty" />
        ) : (
          <>
            <p className="text-sm text-muted-foreground" data-testid="search-category-total">
              {items.length} result{items.length === 1 ? "" : "s"}
            </p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((x) => {
                const itemId = section.id(x);
                return (
                  <ItemCard
                    key={`${section.key}-${itemId}`}
                    to={section.to(x)}
                    src={section.src(x)}
                    round={section.round}
                    Icon={section.Icon}
                    title={section.title(x)}
                    subtitle={section.sub(x)}
                    testid={`search-category-result-${section.key}-${itemId}`}
                    trackRef={bindImpression(x.analytics)}
                    onTrackClick={x.analytics ? () => trackResult("search_result_clicked", x.analytics) : undefined}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
